/**
 * Logika murni section Golden Ticket — dipisah dari komponen supaya aturan yang
 * gampang salah (kategori mana yang punya golden ticket, format tanggal, bentuk
 * baris tabel) bisa dites tanpa nge-render apa pun.
 */

export interface LeaderboardRow {
  id: number;
  rank: number;
  schoolName: string;
  isQualified: boolean;
}

/** Bentuk satu item dari GET event/golden-ticket-schools. */
export interface GoldenTicketSchool {
  id: number;
  npsn?: string;
  school_category?: string;
  school_name?: string;
  is_qualified?: boolean;
  rank?: number;
  is_registered_web?: boolean;
}

const BULAN = [
  "Januari",
  "Februari",
  "Maret",
  "April",
  "Mei",
  "Juni",
  "Juli",
  "Agustus",
  "September",
  "Oktober",
  "November",
  "Desember",
];

/**
 * Kategori sekolah -> query param endpoint. null berarti kategori itu ga punya
 * program golden ticket (Kampus), jadi section-nya ga dirender dan endpoint-nya
 * ga usah dipanggil.
 */
export const categoryParam = (schoolCategory?: string): "smp" | "sma" | null => {
  const normalized = String(schoolCategory ?? "")
    .trim()
    .toLowerCase();

  if (normalized === "smp" || normalized === "sma") return normalized;

  return null;
};

/**
 * "2026-09-24 16:25:22" -> "24 September 2026".
 *
 * Ditulis manual, bukan moment: locale moment di app ini ga di-set ke id, jadi
 * hasilnya bakal bahasa Inggris. Jam sengaja dibuang — Figma cuma nampilin tanggal.
 */
export const formatLastUpdated = (raw?: string): string => {
  if (!raw) return "";

  // Spasi diganti "T" biar Safari mau parse; format "YYYY-MM-DD HH:mm:ss"
  // dianggap invalid di sana.
  const parsed = new Date(String(raw).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return "";

  return `${parsed.getDate()} ${BULAN[parsed.getMonth()]} ${parsed.getFullYear()}`;
};

/**
 * Leaderboard cuma tampil selama pendaftaran kota user masih buka, dilihat dari
 * `registration_end_date` ("2026-10-08 23:59:00") sampai ke menitnya. Bukan
 * flag `is_registration_open`: di CMS flag itu nyangkut 1 buat semua kota,
 * termasuk yang tutup dari 2025.
 *
 * Tanggal kosong / ga kebaca = dianggap tutup, jadi section ga sempat nongol lalu
 * hilang selama data kota belum kebawa.
 * ponytail: diparse sebagai jam device — user WITA/WIT bisa geser 1-2 jam dari
 * jam CMS; tambah offset +07:00 kalau batas tutupnya harus presisi.
 */
export const isRegistrationOpen = (endDate?: string | null, now: Date = new Date()): boolean => {
  if (!endDate) return false;

  // Spasi -> "T" biar Safari mau parse, sama kayak formatLastUpdated.
  const end = new Date(String(endDate).replace(" ", "T"));
  return !Number.isNaN(end.getTime()) && now <= end;
};

/**
 * Stempel waktu watermark: "30/09/2026 16:47" — angka biar pendek. Pakai jam
 * device (bukan server) dan tanpa label zona: user tersebar di WIB/WITA, dan
 * tujuannya cuma nandain kapan screenshot diambil, bukan jadi bukti forensik.
 */
export const formatWatermarkTime = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");

  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
};

/**
 * Response API -> baris tabel. Diurutkan ulang dari rank terkecil: tampilan
 * leaderboard ga boleh ikut-ikutan kalau suatu saat urutan kiriman API berubah.
 */
export const toLeaderboardRows = (schools?: GoldenTicketSchool[]): LeaderboardRow[] => {
  if (!Array.isArray(schools)) return [];

  return [...schools]
    .sort((a, b) => Number(a?.rank ?? 0) - Number(b?.rank ?? 0))
    .map((school) => ({
      id: Number(school?.id),
      rank: Number(school?.rank ?? 0),
      schoolName: String(school?.school_name ?? "-"),
      isQualified: Boolean(school?.is_qualified),
    }));
};

/**
 * Baris-baris watermark -> background CSS yang berulang, miring, dan tipis.
 * Teks di-escape biar karakter kayak & atau < (nama sekolah bisa aja) ga
 * ngerusak SVG-nya.
 *
 * Sengaja pendek, 7% opacity, dan jarang-jarang (tile lega): cukup buat nandain
 * pemilik screenshot tanpa bikin tabelnya ramai — pola yang sama dipakai
 * watermark Prisma Browser/Zoom. Ukuran tile ngikut baris terpanjang (±6.5px/huruf
 * di 11px bold); teks miring -20° makan tinggi ±0.34× panjangnya.
 *
 * `textHeight` = tinggi tumpukan teks miringnya (tile minus jarak 80px antar-tile).
 * Badan tabel minimal setinggi ini: tabel satu baris cuma ±48px, dan teksnya ada
 * di tengah tile — tanpa ini yang kelihatan cuma pojok tile yang kosong.
 */
export const watermarkBackground = (
  lines: string[],
): { backgroundImage: string; textHeight: number } => {
  const esc = (t: string) => t.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  const len = Math.max(...lines.map((line) => line.length)) * 6.5;
  const w = Math.round(Math.max(260, len + 100));
  const h = Math.round(Math.max(160, len * 0.36 + lines.length * 15 + 80));
  // Baris pertama digeser ke atas setengah tinggi blok biar tumpukannya di tengah tile.
  const tspans = lines
    .map((line, i) => {
      const dy = i ? "1.3em" : `${-(lines.length - 1) * 0.65}em`;
      return `<tspan x="50%" dy="${dy}">${esc(line)}</tspan>`;
    })
    .join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><text y="50%" text-anchor="middle" dominant-baseline="middle" transform="rotate(-20 ${w / 2} ${h / 2})" font-family="sans-serif" font-size="11" font-weight="700" fill="#1f2d3d" fill-opacity="0.07">${tspans}</text></svg>`;
  return {
    backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
    textHeight: h - 80,
  };
};
